// =============================================================================
// ui/shell/Icon.tsx — one <svg> wrapper for the whole icon set.
// -----------------------------------------------------------------------------
// The inner markup lives in `icons.ts`, extracted from the old client by script.
// The wrapper is written once here so viewBox, stroke width and sizing cannot
// drift between icons — which is exactly what happens when twenty SVGs are each
// pasted in with their own attributes.
//
// ⚠ `dangerouslySetInnerHTML` is correct here and only here. The content is a
// compile-time constant from our own source file; no value on this path is user
// data, or reachable from user data. Hand-converting twenty SVGs to JSX (every
// `stroke-width` → `strokeWidth`, every `stroke-linecap` → `strokeLinecap`) is
// twenty chances to silently drop an attribute, and a subtly wrong icon is the
// defect nobody reports and everybody sees.
// =============================================================================

import { ICON_INNER } from './icons';

/**
 * ── 🚨 THE SIZE IS ON THE ELEMENT, NOT LEFT TO THE CALLER ──
 *
 * An inline `<svg>` with no width or height is **300×150 px** — the SVG spec's
 * default — so an unsized icon does not render small, it renders enormous and
 * takes the page with it. This component shipped with no size at all, and got
 * away with it because its only caller was the sidebar, which sizes it from
 * `.sidebar .nav-item svg`. The second caller put one in a heading and drew a
 * document glyph the height of the viewport.
 *
 * ⚠ These are PRESENTATION ATTRIBUTES, which lose to any CSS rule — so every
 * existing `… svg { width: … }` still wins and nothing that was sized changes.
 * What they fix is the case nobody remembered to write a rule for.
 */
export function Icon({ name, className }: { name: string; className?: string }) {
  const inner = ICON_INNER[name];
  // ⚠ Renders NOTHING for an unknown name rather than a placeholder box. A tab
  // whose icon key is wrong should look like a tab with no icon, not like a
  // broken image — and the label is always beside it.
  if (!inner) return null;
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}
